// The link in the "account moved" email (F-IAM-13, spec/15 §2): what a café moved for the player from its previous
// system, and claiming it into their own account.
//
// The link carries a signed token in its fragment (`/moved#<token>`), so it never reaches a server log or a Referer;
// the page keeps it in session storage across signing in and sends it only in a request body. Anyone with the link may
// see what waits, masked; only the account whose verified email is exactly the one the link went to can claim it.

import { useTranslation } from '@clavian/i18n/react';
import { Button, Skeleton } from '@clavian/ui-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { PROBLEM, problemCode } from '../api/problems';
import { useSession } from '../api/queries';
import { useProblem } from '../api/use-problem';
import { PublicFrame } from '../shell/PublicFrame';

const TOKEN_KEY = 'clavian.moved.token';

/** The token from the link's fragment, kept for the round trip through sign-in. Never put back in a URL. */
function readToken(): string | null {
  try {
    const fromHash = window.location.hash.replace(/^#/, '');
    if (fromHash.length >= 32) {
      window.sessionStorage.setItem(TOKEN_KEY, fromHash);
      window.history.replaceState(null, '', window.location.pathname);
      return fromHash;
    }
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function MovedPage(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const problem = useProblem();
  const queryClient = useQueryClient();
  const session = useSession();
  const navigate = useNavigate();
  const [token] = useState(readToken);
  const link = useQuery({
    queryKey: ['carried-over', 'link', token ?? ''] as const,
    queryFn: () => api.readCarriedOverLink({ body: { token: token ?? '' } }),
    enabled: token !== null,
    retry: false,
  });
  const claim = useMutation({
    mutationFn: () => api.claimCarriedOverByLink({ body: { token: token ?? '' } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['carried-over', 'link'] }),
  });

  const format = (amount: number, currency: string) => new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount / 100);
  const duration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours} h${minutes > 0 ? ` ${minutes} min` : ''}` : `${minutes} min`;
  };
  const day = (date: string) => new Intl.DateTimeFormat('en-IN', { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(`${date}T12:00:00.000Z`));

  const body = (() => {
    if (token === null) return <p className="pw-moved__notice" role="alert">{t('moved.invalid')}</p>;
    if (link.isError) return <p className="pw-moved__notice" role="alert">{problem.text(link.error, { fallback: t('moved.invalid') })}</p>;
    if (link.data === undefined) return <Skeleton height="10rem" />;
    const data = link.data;
    const venue = data.venueName;
    const amounts = data.carriedOver;
    const failure = claim.isError ? problemCode(claim.error) : undefined;
    // The two refusals that name the email or the venue are worded here; every other one says what its code says.
    const failureText =
      failure === PROBLEM.carriedOverEmailMismatch
        ? t('moved.mismatch', { email: data.email })
        : failure === PROBLEM.carriedOverCounterOnly
          ? t('moved.counterOnly', { venue })
          : problem.text(claim.error, { fallback: t('moved.failed') });
    return (
      <>
        <p className="pw-muted">{t('moved.lead')}</p>
        {amounts.cash.amount === 0 && amounts.timeSeconds === 0 && data.state === 'waiting' ? (
          <p>{t('moved.nothing')}</p>
        ) : data.state === 'waiting' ? (
          <dl className="pw-moved__amounts">
            {amounts.cash.amount > 0 ? (
              <div>
                <dt>{t('moved.money')}</dt>
                <dd>{format(amounts.cash.amount, amounts.cash.currency)}</dd>
              </div>
            ) : null}
            {amounts.cashTaxPaid.amount > 0 ? (
              <div>
                <dt>{t('moved.moneyTaxPaid')}</dt>
                <dd>{format(amounts.cashTaxPaid.amount, amounts.cashTaxPaid.currency)}</dd>
              </div>
            ) : null}
            {amounts.timeSeconds > 0 ? (
              <div>
                <dt>{t('moved.time')}</dt>
                <dd>
                  {duration(amounts.timeSeconds)}
                  {amounts.timeValidUntil === null ? null : <span className="pw-muted"> {t('moved.timeUntil', { date: day(amounts.timeValidUntil) })}</span>}
                </dd>
              </div>
            ) : null}
            <div>
              <dt>{t('moved.claimBy')}</dt>
              <dd>{day(data.claimWindowEndsOn)}</dd>
            </div>
          </dl>
        ) : null}

        {claim.isSuccess ? (
          <div className="pw-moved__done" role="status">
            <p>{t('moved.claimed', { venue })}</p>
            <Link to="/wallet" className="pw-link">
              {t('moved.wallet')}
            </Link>
          </div>
        ) : data.state === 'claimed' ? (
          <p role="status">{t('moved.alreadyClaimed')}</p>
        ) : data.counterOnly ? (
          <p className="pw-moved__notice">{t('moved.counterOnly', { venue })}</p>
        ) : data.state === 'closed' ? (
          <p className="pw-moved__notice">{t('moved.closed')}</p>
        ) : (
          <section className="pw-moved__claim" aria-labelledby="pw-moved-how">
            <h2 id="pw-moved-how">{t('moved.howTitle')}</h2>
            <p>{t('moved.how', { email: data.email })}</p>
            {failure === undefined ? null : (
              <p className="pw-moved__notice" role="alert">
                {failureText}
              </p>
            )}
            {session.data == null ? (
              <Button variant="primary" onClick={() => void navigate({ to: '/sign-in', search: { redirect: '/moved' } })}>
                {t('moved.signIn')}
              </Button>
            ) : (
              <Button variant="primary" busy={claim.isPending} onClick={() => claim.mutate()}>
                {t('moved.claim')}
              </Button>
            )}
          </section>
        )}
      </>
    );
  })();

  return (
    <PublicFrame>
      <div className="pw-page pw-moved">
        <header className="pw-page__head">
          <h1 className="pw-page__title">{link.data?.venueName ? t('moved.title', { venue: link.data.venueName }) : t('moved.titleNoVenue')}</h1>
        </header>
        <div aria-busy={link.isLoading} aria-label={link.isLoading ? t('moved.loading') : undefined}>
          {body}
        </div>
      </div>
    </PublicFrame>
  );
}
